import React from 'react';
import { useWatch } from 'react-hook-form';
import type { FormFileListNode } from '../../types/layout';
import { useFormEngineForm } from '../../context/FormEngineContext';
import { DocumentChip } from '@packages/ui/primitives/DocumentChip';
import { Stack } from '@packages/ui/layout';
import { Typography } from '@packages/ui/primitives';
import { ActionRegistry } from '@packages/table-builder/registry';

interface FileEntry {
  id?: string;
  fileName?: string;
  name?: string;
  fileType?: string;
  documentId?: string;
}

function getFileName(file: FileEntry): string {
  return file.fileName ?? file.name ?? 'Document';
}

function getFileIcon(file: FileEntry): string {
  const name = getFileName(file).toLowerCase();
  if (name.endsWith('.pdf')) return 'picture_as_pdf';
  if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.csv')) return 'table_chart';
  if (name.endsWith('.doc') || name.endsWith('.docx')) return 'description';
  if (name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image';
  return 'draft';
}

interface FormFileListRendererProps {
  node: FormFileListNode;
}

/**
 * Renders a list of files/documents from the view data context.
 * Reads `node.dataKey` from RHF state (hydrated by lifecycleProcessor)
 * and renders downloadable `DocumentChip` entries.
 */
export const FormFileListRenderer = React.memo(function FormFileListRenderer({
  node,
}: FormFileListRendererProps): React.ReactElement {
  const { form } = useFormEngineForm();
  const files = useWatch({ control: form.control, name: node.dataKey }) as FileEntry[] | undefined;

  if (!files?.length) {
    return (
      <div data-view-file-list={node.dataKey}>
        <Typography variant='body-s' color='textSubtle'>
          No documents available
        </Typography>
      </div>
    );
  }

  const handleDownload = (file: FileEntry) => {
    const action = ActionRegistry.getAction(`DOWNLOAD_${node.dataKey.toUpperCase()}`);
    if (action) {
      void (action as any).execute?.({ fileId: file.id ?? file.documentId, fileName: getFileName(file) });
    }
  };

  const isCompact = node.variant === 'compact';

  return (
    <div data-view-file-list={node.dataKey}>
      <Stack gap={isCompact ? 4 : 8}>
        {files.map((file, idx) => (
          <DocumentChip
            key={file.id ?? file.documentId ?? idx}
            icon={getFileIcon(file)}
            size={isCompact ? 'sm' : 'md'}
            onClick={() => handleDownload(file)}
            style={{ cursor: 'pointer' }}
          >
            {getFileName(file)}
          </DocumentChip>
        ))}
      </Stack>
    </div>
  );
});
